import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Ban,
  CheckCircle2,
  Download,
  ExternalLink,
  Loader2,
  LogIn,
  Mail,
  Plus,
  RefreshCw,
  Search,
  Send,
  UserPlus,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import CommercialeInvitoDialog from "./CommercialeInvitoDialog";
import AdminPagamentoCard from "./AdminPagamentoCard";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale`;
const STATI = [
  { id: "", label: "Tutti gli stati" },
  { id: "in_attesa_registrazione", label: "In attesa registrazione" },
  { id: "registrazione_avviata", label: "Registrazione avviata" },
  { id: "in_attesa_pagamento_stripe", label: "Pagamento Stripe in attesa" },
  { id: "in_attesa_bonifico", label: "Bonifico in attesa" },
  { id: "checkout_fallito", label: "Checkout Stripe fallito" },
  { id: "pagamento_scaduto", label: "Pagamento Stripe scaduto" },
  { id: "abbonamento_cancellato", label: "Abbonamento cancellato" },
  { id: "attivato", label: "Account attivo" },
  { id: "scaduto", label: "Scaduto" },
  { id: "annullato", label: "Annullato" },
];
const FLOW_STEPS = [
  { id: "in_attesa_registrazione", label: "Invito inviato" },
  { id: "registrazione_avviata", label: "Registrazione avviata" },
  { id: "attivato", label: "Account attivo" },
];

function errorMessage(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function statusMeta(status) {
  if (status === "attivato") return { label: "Account attivo", className: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (status === "in_attesa_bonifico") return { label: "Bonifico in attesa", className: "bg-amber-100 text-amber-900 border-amber-200" };
  if (status === "in_attesa_pagamento_stripe") return { label: "Pagamento Stripe in attesa", className: "bg-sky-100 text-sky-800 border-sky-200" };
  if (status === "checkout_fallito") return { label: "Checkout Stripe fallito", className: "bg-rose-100 text-rose-800 border-rose-200" };
  if (status === "pagamento_scaduto") return { label: "Pagamento Stripe scaduto", className: "bg-orange-100 text-orange-800 border-orange-200" };
  if (status === "abbonamento_cancellato") return { label: "Abbonamento cancellato", className: "bg-rose-100 text-rose-800 border-rose-200" };
  if (status === "registrazione_avviata") return { label: "Registrazione avviata", className: "bg-violet-100 text-violet-800 border-violet-200" };
  if (status === "scaduto") return { label: "Scaduto", className: "bg-slate-100 text-slate-700 border-slate-200" };
  if (status === "annullato") return { label: "Annullato", className: "bg-rose-100 text-rose-800 border-rose-200" };
  return { label: "In attesa registrazione", className: "bg-amber-100 text-amber-800 border-amber-200" };
}

function isSuperAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem("super_admin_user") || "{}");
    return user.scope !== "crm_operatore";
  } catch {
    return false;
  }
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function stepIndex(status) {
  if (status === "attivato" || status === "pagamento_scaduto" || status === "abbonamento_cancellato") return 2;
  if (status === "registrazione_avviata" || status === "in_attesa_bonifico" || status === "in_attesa_pagamento_stripe" || status === "checkout_fallito") return 1;
  if (status === "annullato" || status === "scaduto") return -1;
  return 0;
}

function statoUtenzaHint(invito) {
  const utente = invito?.utente;
  if (invito?.stato_flusso === "pagamento_scaduto" || invito?.pagamento?.stripe_past_due) {
    return "Pagamento Stripe non riuscito";
  }
  if (invito?.stato_flusso === "abbonamento_cancellato" || invito?.pagamento?.stripe_canceled) {
    return "Abbonamento cancellato";
  }
  if (invito?.stato_flusso === "in_attesa_pagamento_stripe" || invito?.pagamento?.pending_stripe) {
    return "Registrazione avviata · checkout Stripe in corso";
  }
  if (invito?.stato_flusso === "checkout_fallito") {
    return "Checkout Stripe non riuscito";
  }
  if (invito?.stato_flusso === "in_attesa_bonifico" || utente?.pending_bank_transfer) {
    return "Registrazione completata · bonifico da confermare";
  }
  if (utente?.id) {
    return utente.is_active ? "Utenza attiva" : "Utenza creata, non ancora attiva";
  }
  return invito?.invio?.inviato ? "Email inviata" : "Email da reinviare";
}

function InvitoUtenzaCard({ invito, acting, onActivate, onImpersonate, onLoginOverride }) {
  const utente = invito?.utente;
  if (!utente?.id) return null;
  const superAdmin = isSuperAdmin();
  const pendingBank = utente.pending_bank_transfer;
  const strutture = utente.strutture || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Utenza Affidya</p>
          <p className="font-medium text-slate-900">{utente.nome || utente.email || "—"}</p>
          <p className="text-xs text-slate-500">{utente.email}</p>
        </div>
        {pendingBank ? (
          <Badge className="bg-amber-100 text-amber-900 border-amber-200">Bonifico in attesa</Badge>
        ) : (
          <Badge className={utente.is_active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
            {utente.is_active ? "Attiva" : "Non attiva"}
          </Badge>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-slate-500">Ruolo</dt>
          <dd className="font-medium capitalize">{utente.role || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Stato org</dt>
          <dd className="font-medium">{utente.facility_status_label || utente.facility_status || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Piano</dt>
          <dd className="font-medium">{[utente.subscription_label, utente.plan_label].filter(Boolean).join(" · ") || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Pagamento</dt>
          <dd className="font-medium">{utente.org_payment_method === "bank_transfer" ? "Bonifico" : (utente.org_payment_method || "—")}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-slate-500">Strutture</dt>
          <dd className="font-medium">{strutture.length ? strutture.map((item) => item.name).join(", ") : (utente.facility_name || "—")}</dd>
        </div>
      </dl>
      {pendingBank ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          Il referente ha completato la registrazione. Attiva l&apos;utenza per sbloccare l&apos;account dopo la verifica del bonifico.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={utente.is_active}
            disabled={acting || utente.login_access_override}
            onCheckedChange={(checked) => onActivate(invito, checked)}
          />
          <span className="text-xs text-slate-700">{utente.is_active ? "Utenza attiva" : "Utenza disattiva"}</span>
        </div>
        {pendingBank && !utente.is_active ? (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={acting} onClick={() => onActivate(invito, true)}>
            {acting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
            Attiva utenza
          </Button>
        ) : null}
        {superAdmin ? (
          <>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!utente.login_access_override}
                disabled={acting}
                onCheckedChange={(checked) => onLoginOverride(invito, checked)}
              />
              <span className="text-xs text-slate-700">Accesso forzato</span>
            </div>
            {(utente.is_active || utente.login_access_override) ? (
              <Button size="sm" variant="outline" disabled={acting} onClick={() => onImpersonate(invito)}>
                <LogIn className="mr-1 h-4 w-4" /> Login come utente
                <ExternalLink className="ml-1 h-3 w-3 opacity-60" />
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function OnboardingInvitiAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const res = await fetch(`${BASE_URL}/inviti?${params}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Impossibile caricare gli inviti"));
      setItems(json.data || []);
    } catch (error) {
      toast.error(error.message || "Errore nel caricamento degli inviti");
    } finally {
      setLoading(false);
    }
  }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openDetail = async (invito) => {
    setDetailLoading(true);
    setDetail(invito);
    try {
      const res = await fetch(`${BASE_URL}/inviti/${invito.id}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Dettaglio non disponibile"));
      setDetail(json);
    } catch (error) {
      toast.error(error.message || "Errore nel dettaglio");
    } finally {
      setDetailLoading(false);
    }
  };

  const act = async (invito, action) => {
    const path = action === "reinvia" ? "reinvia" : "annulla";
    if (action === "annulla" && !window.confirm(`Annullare l'invito a ${invito.email}?`)) return;
    setActingId(invito.id);
    try {
      const res = await fetch(`${BASE_URL}/inviti/${invito.id}/${path}`, { method: "POST", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Operazione non riuscita"));
      toast.success(action === "reinvia" ? (json.invito_inviato ? "Invito rinviato" : "Reinvio non riuscito") : "Invito annullato");
      load();
      if (detail?.id === invito.id) openDetail(invito);
    } catch (error) {
      toast.error(error.message || "Errore");
    } finally {
      setActingId(null);
    }
  };

  const applyInvitoUpdate = (invito) => {
    if (!invito?.id) return;
    setItems((current) => current.map((item) => (item.id === invito.id ? { ...item, ...invito } : item)));
    if (detail?.id === invito.id) setDetail(invito);
  };

  const activateUser = async (invito, isActive) => {
    const utente = invito?.utente;
    if (!utente?.id) return;
    if (isActive && utente.pending_bank_transfer && !window.confirm(
      `Attivare l'utenza di ${utente.email || invito.email}?\n\nL'organizzazione verrà sbloccata e verrà inviata l'email di conferma al referente.`,
    )) return;
    if (!isActive && !window.confirm(`Disattivare l'utenza di ${utente.email || invito.email}?`)) return;
    setActingId(invito.id);
    try {
      const res = await fetch(`${BASE_URL}/inviti/${invito.id}/attiva-utenza`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: isActive }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Impossibile aggiornare l'utenza"));
      toast.success(json.message || (isActive ? "Utenza attivata" : "Utenza disattivata"));
      if (json.invito) applyInvitoUpdate(json.invito);
      else load();
      if (detail?.id === invito.id && json.invito) setDetail(json.invito);
      else if (detail?.id === invito.id) openDetail(invito);
    } catch (error) {
      toast.error(error.message || "Errore nell'attivazione");
    } finally {
      setActingId(null);
    }
  };

  const impersonateUser = async (invito) => {
    const userId = invito?.utente?.id;
    if (!userId) return;
    setActingId(invito.id);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/impersonate`, { method: "POST", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Impersonazione non riuscita"));
      const impersonateUrl = new URL(window.location.origin);
      impersonateUrl.pathname = "/";
      impersonateUrl.searchParams.set("impersonate_token", json.token);
      impersonateUrl.searchParams.set("impersonate_refresh", json.refresh_token);
      window.open(impersonateUrl.toString(), "_blank");
      toast.success(`Login come ${invito.utente?.nome || invito.utente?.email} aperto in nuova scheda`);
    } catch (error) {
      toast.error(error.message || "Errore durante l'impersonazione");
    } finally {
      setActingId(null);
    }
  };

  const setLoginOverride = async (invito, enabled) => {
    const userId = invito?.utente?.id;
    if (!userId) return;
    const msg = enabled
      ? "Abilitare l'accesso forzato? L'utente potrà fare login anche con status INACTIVE."
      : "Disabilitare l'accesso forzato?";
    if (!window.confirm(msg)) return;
    setActingId(invito.id);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/login-access-override`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Impossibile aggiornare l'accesso forzato"));
      toast.success(json.message || (enabled ? "Accesso forzato attivo" : "Accesso forzato disattivato"));
      if (detail?.id === invito.id) openDetail(invito);
      load();
    } catch (error) {
      toast.error(error.message || "Errore accesso forzato");
    } finally {
      setActingId(null);
    }
  };

  const syncStripe = async (invito) => {
    if (!invito?.id) return;
    setActingId(invito.id);
    try {
      const res = await fetch(`${BASE_URL}/inviti/${invito.id}/sync-stripe`, { method: "POST", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Impossibile leggere Stripe"));
      toast.success("Stato Stripe aggiornato");
      if (json.invito) applyInvitoUpdate({ ...json.invito, pagamento: json.pagamento || json.invito.pagamento });
      else if (detail?.id === invito.id && json.pagamento) {
        setDetail((current) => (current ? { ...current, pagamento: json.pagamento } : current));
      }
    } catch (error) {
      toast.error(error.message || "Errore Stripe");
    } finally {
      setActingId(null);
    }
  };

  const downloadAllegato = async (invitoId, tipo, filename) => {
    try {
      const res = await fetch(`${BASE_URL}/inviti/${invitoId}/allegati/${tipo}`, { headers });
      if (!res.ok) throw new Error("Download non riuscito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || tipo;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || "Errore nel download");
    }
  };

  const current = detail?.stato_flusso || detail?.stato;
  const activeStep = stepIndex(current);

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={UserPlus}
        title="Inviti onboarding"
        description="Crea l'account struttura, invia il link di registrazione e segui lo stato fino all'attivazione."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Nuovo invito
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs">Cerca</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="pl-8" value={q} onChange={(event) => setQ(event.target.value)} placeholder="Struttura, referente, email…" />
          </div>
        </div>
        <div className="w-56">
          <Label className="text-xs">Stato flusso</Label>
          <Select value={status || "__all__"} onValueChange={(value) => setStatus(value === "__all__" ? "" : value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATI.map((item) => <SelectItem key={item.id || "all"} value={item.id || "__all__"}>{item.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="admin-table-sticky-actions w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-3">Struttura</th>
                  <th className="p-3">Referente</th>
                  <th className="p-3">Utenza</th>
                  <th className="p-3">Stato</th>
                  <th className="p-3">Allegati</th>
                  <th className="p-3">Scade</th>
                  <th className="p-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan="7" className="p-8 text-center text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</td></tr>
                ) : !items.length ? (
                  <tr><td colSpan="7" className="p-8 text-center text-slate-500">Nessun invito. Crea un account e invia il link al referente.</td></tr>
                ) : items.map((invito) => {
                  const meta = statusMeta(invito.stato_flusso || invito.stato);
                  const prefill = invito.prefill || {};
                  const utente = invito.utente;
                  const canResend = !["attivato", "annullato"].includes(invito.stato) && !utente?.id;
                  return (
                    <tr key={invito.id} className="hover:bg-slate-50">
                      <td className="p-3">
                        <p className="font-medium">{prefill.nome || "—"}</p>
                        <p className="text-xs text-slate-500">{[prefill.comune, prefill.provincia].filter(Boolean).join(" ") || "—"}</p>
                      </td>
                      <td className="p-3">
                        <p>{prefill.referente_nome || "—"}</p>
                        <p className="text-xs text-slate-500">{invito.email}</p>
                      </td>
                      <td className="p-3">
                        {utente?.id ? (
                          <>
                            <p className="font-medium">{utente.nome || utente.email}</p>
                            <p className="text-xs text-slate-500">{utente.facility_status_label || utente.facility_status || "—"}</p>
                          </>
                        ) : invito.pagamento ? (
                          <>
                            <p className="font-medium">{invito.pagamento.method_label}</p>
                            <p className="text-xs text-slate-500">{invito.pagamento.transaction_status_label || invito.pagamento.org_status_label || "—"}</p>
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">Non ancora creata</p>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge className={meta.className}>{meta.label}</Badge>
                        <p className="mt-1 text-[11px] text-slate-500">{statoUtenzaHint(invito)}</p>
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        {(invito.allegati || []).length
                          ? invito.allegati.map((item) => item.tipo_label).join(", ")
                          : "—"}
                      </td>
                      <td className="p-3">{formatDate(invito.expires_at)}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(invito)}>Dettaglio</Button>
                          {utente?.can_activate ? (
                            <Button
                              size="sm"
                              className="h-8 bg-emerald-600 px-2 text-xs hover:bg-emerald-700"
                              disabled={actingId === invito.id}
                              onClick={() => activateUser(invito, true)}
                            >
                              {actingId === invito.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                              Attiva
                            </Button>
                          ) : null}
                          {canResend ? (
                            <Button size="icon" variant="ghost" title="Rinvia email" disabled={actingId === invito.id} onClick={() => act(invito, "reinvia")}>
                              <Send className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canResend ? (
                            <Button size="icon" variant="ghost" title="Annulla" disabled={actingId === invito.id} onClick={() => act(invito, "annulla")}>
                              <Ban className="h-4 w-4 text-rose-600" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CommercialeInvitoDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />

      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Flusso invito · {detail?.prefill?.nome || detail?.email}</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</div>
          ) : detail ? (
            <div className="space-y-4 text-sm">
              <ol className="grid grid-cols-3 gap-2">
                {FLOW_STEPS.map((step, index) => {
                  const done = activeStep >= index;
                  const currentStep = activeStep === index;
                  return (
                    <li key={step.id} className={`rounded-lg border p-2 text-center text-xs ${done ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-500"}`}>
                      <span className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${done ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>
                        {index + 1}
                      </span>
                      <p className={currentStep ? "font-semibold" : ""}>{step.label}</p>
                    </li>
                  );
                })}
              </ol>
              {current === "scaduto" || current === "annullato" ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  Flusso interrotto: {statusMeta(current).label}. Puoi {current === "scaduto" ? "rinviare l'invito" : "crearne uno nuovo"}.
                </p>
              ) : null}
              <InvitoUtenzaCard
                invito={detail}
                acting={actingId === detail.id}
                onActivate={activateUser}
                onImpersonate={impersonateUser}
                onLoginOverride={setLoginOverride}
              />
              <AdminPagamentoCard
                pagamento={detail.pagamento}
                refreshing={actingId === detail.id}
                onRefresh={() => syncStripe(detail)}
              />
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div><dt className="text-slate-500">Referente</dt><dd className="font-medium">{detail.prefill?.referente_nome || "—"}</dd></div>
                <div><dt className="text-slate-500">Email invito</dt><dd className="font-medium">{detail.email}</dd></div>
                <div><dt className="text-slate-500">Creato</dt><dd>{formatDate(detail.created_at)}</dd></div>
                <div><dt className="text-slate-500">Scade</dt><dd>{formatDate(detail.expires_at)}</dd></div>
              </dl>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Allegati inviati</p>
                {(detail.allegati || []).length ? (
                  <ul className="space-y-1">
                    {detail.allegati.map((item) => (
                      <li key={item.tipo} className="flex items-center justify-between rounded-lg border border-slate-100 px-2 py-1.5 text-xs">
                        <span>{item.tipo_label}: {item.filename}</span>
                        <Button size="icon" variant="ghost" onClick={() => downloadAllegato(detail.id, item.tipo, item.filename)}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-slate-500">Nessun allegato.</p>}
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Cronologia</p>
                <ul className="max-h-48 space-y-1 overflow-y-auto">
                  {(detail.timeline || []).slice().reverse().map((event) => (
                    <li key={event.id || event.at} className="rounded-md bg-slate-50 px-2 py-1.5 text-xs">
                      <p className="font-medium text-slate-800">{event.nota}</p>
                      <p className="text-slate-500">{formatDate(event.at)} {event.operatore ? `· ${event.operatore}` : ""}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {detail.stato !== "attivato" && detail.stato !== "annullato" && !detail.utente?.id ? (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => act(detail, "reinvia")}><Mail className="mr-1 h-4 w-4" />Rinvia email</Button>
                  <Button size="sm" variant="outline" className="text-rose-700" onClick={() => act(detail, "annulla")}>Annulla</Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
